import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid"
import { primaryOrderRowsData, popupType, ManageOutletApprovalRowData, orderStatusConfig, orderSourceConfig } from "../../types/types";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { InputLabel, MenuItem, FormControl, Select } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import "./ManageOutletApproval.css"

import dayjs, { Dayjs } from "dayjs";
import {
  Button,
  Stack,
  TextField,
} from "@mui/material";
import {
  getOutletListForApproval,
  getUTCDate,
} from "../../services/manageOrderService";
import "../primaryOrder/PrimaryOrder.css";
import "../viewOrder/ViewOrder.css";
import "../manageBasket/ManageBasket.css";
import { Loader } from "../../components/loader/Loader";

import { filterData, searchWithStatusFilter, updateDatesInLocalStorage } from "../../components/dms_common/DmsCommonServices";
import Header from "../../components/dms_common/Header";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import { getConfigData } from "../../services/configurationService";
import { logEvent } from "firebase/analytics";
import { analytics, logLoadTrace } from "../../../src/firebase";
import { fetchSessionStorageFilter, updateSessionStorageFilter } from "../../components/dms_common/DmsUtils";
import { FilterKeys } from "../../types/types";
import { outletApprovalColumnsMetaData } from "./ManageSecondaryOrderUtils";
import ApprovalSummary from "../viewOutletDetails/components/ApprovalSummary";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";

const ManageOutletApproval = () => {
  const { storedStartDate, storedEndDate, storeSessionStorageDates } = useSessionStorageDates("outletApproval")
  const loginId = JSON.parse(localStorage.authContext).user.loginId;
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");

  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs>(storedStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(storedEndDate);
  const [data, setData] = useState<ManageOutletApprovalRowData[]>([]);
  const [rows, setRows] = useState<ManageOutletApprovalRowData[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [outletApprovalTrackingStages, setOutletApprovalTrackingStages] = useState<orderStatusConfig[]>([]);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
        initialGenericModalProps
      );
  const [loader, setLoader] = useState(false);
  const isCompanyPortal = useRef<boolean>(false);
  const orderSourcesRef = useRef<Record<string, orderSourceConfig>>({});
  const designation = JSON.parse(localStorage.authContext).user.designation || [];

  const [searchedValue, setSearchedValue] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [resetSearch, setResetSearch] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); 
  const [pendingApprovals, setPendingApprovals] = useState<number>(0);
  const [totalApprovals, setTotalApprovals] = useState<number>(0);
  const [outletApprovalData, setOutletApprovalData] = useState<any>();
  const navbarParentLabel = "Purchase";
  const pageID = "PrimaryOrder";
  const outletLocationTransformerName = useRef<string>("outlet_location_transformer");

  const openPopup = useCallback((message: string, type: popupType) => {
    setPopupType(type);
    setGenericMessage(message);
    setOpenGenericModal(true);
  }, []);

  const isStartDateIsGreaterThanEndDate = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    const diff = endDate.diff(startDate);
    return diff < 0;
  };

  const isGapMoreThanOneMonth = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    const diffInDays = endDate.diff(startDate, "days");
    return diffInDays > 31;
  };

  function canWeMakeRequest() {
    if (startDate && endDate && isGapMoreThanOneMonth(startDate, endDate)) {
      openPopup("You can't select date range more than one month!", "Alert");
      return false;
    }
    if (startDate && endDate && isStartDateIsGreaterThanEndDate(startDate, endDate)) {
      openPopup("To Date should be greater than from date.", "Alert");
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, navbarParentLabel, pageID);
    return true;
  }

  const handleApply = async () => {
    if (isProcessing || !canWeMakeRequest()) return; // Prevent if already processing
    setIsProcessing(true);
    try {
      storeSessionStorageDates(startDate, endDate);
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      await getAllOutletForApprovalData(start, end);
    } finally {
      setIsProcessing(false);
    }
  };
const handleClearAll = async () => { if (isProcessing) return; // Prevent if already processing
    setIsProcessing(true);
    try {
      const date = dayjs();
      storeSessionStorageDates(date, date);
      const start = getUTCDate(date, "start");
      const end = getUTCDate(date, "end");
      setStartDate(date);
      setEndDate(date);
      await getAllOutletForApprovalData(start, end);
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchRequiredConfigs = async () => {
    try {
      const outletTrackingStages = await getConfigData(
        "dms_configuration",
        "outletApprovalTrackingStages"
      );
      const companyPortalLoginIdConfig = await getConfigData(
        "dms_configuration",
        "companyPortalLoginId"
      );
      const orderSourcesConfig = await getConfigData(
        "dms_configuration",
        "orderSources"
      );
      const outletLocationTransformerConfig = await getConfigData(
        "dms_configuration",
        "outletLocationTransformerName"
      );

      const orderSourcesMapping: Record<string, orderSourceConfig> = {};
      orderSourcesConfig[0]?.configValue?.forEach(
        (sourceObj: orderSourceConfig) => {
          orderSourcesMapping[sourceObj.id] = sourceObj;
        }
      );
      orderSourcesRef.current = orderSourcesMapping;
      const companyPortalLoginId = companyPortalLoginIdConfig[0]?.configValue ?? "reportadmin"    
      isCompanyPortal.current = designation.includes(companyPortalLoginId);
      setOutletApprovalTrackingStages(outletTrackingStages[0]?.configValue ?? [])
      outletLocationTransformerName.current = outletLocationTransformerConfig[0]?.configValue ?? "outlet_location_transformer";
    } catch (e) {
      console.error("Error while fetching configs", e);
      setGenericModalProps(
        getGenericModalProps("error", "Error while fetching configs")
      );
    }
  };

 
  useEffect(() => {
    const interval = setInterval(() => {
      getInitalTableData();
    }, 2 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [startDate, endDate]);


  const getAllOutletForApprovalData = async (startDate: string, endDate: string) => {
    setLastRefreshed(dayjs());
    setResetSearch(true); 
    setTimeout(() => setResetSearch(false), 0); 
    let pendingApprovalCount = 0;
    try {
      setLoader(true);
      const [response] = await Promise.all([
          getOutletListForApproval(startDate, endDate, outletLocationTransformerName.current),
        ]);
      const totalApprovalCount = response?.data?.features?.length;

      setTotalApprovals(totalApprovalCount);
      if (response.data.features.length > 0) {
        let responseArray: ManageOutletApprovalRowData[] = [];
        response.data.features.forEach((item: any, index: any) => {
          const {
            id,
            outletName,
            outletCode, 
            immediateParent,
            contactno: outletMobileNumber,
            extendedAttributes = {},
            location,
            approvalStatus: status
          } = item;
          const isKeyAccount = extendedAttributes?.keyAccount === "true";
          const outletSource = extendedAttributes?.source;
          const {salesrep: salesRepId, salesRepName, supplierName} = extendedAttributes;
          const distributorId = extendedAttributes["parent-1-0-id"] ?? extendedAttributes["parent-0-1-id"] ?? extendedAttributes["distributor"] ?? "";
          const uniqueSalesRepId = Array.from(
            new Set(salesRepId?.split(",")?.map((str: any) => str?.trim().toUpperCase()))).join(",");

          const {
            city: outletCity,
            pincode: outletPincode
          } = location;

          if(status && status === "PENDING"){
            pendingApprovalCount += 1;
          }

          responseArray.push({
            id,
            outletName,
            outletCode,
            salesRepName: salesRepName,
            uniqueSalesRepId,
            distributorName: supplierName,
            distributorId,
            outletCity, 
            outletMobileNumber,
            outletPincode,
            outletSource,
            status,
            isKeyAccount: isKeyAccount,
            editIcon: isCompanyPortal.current && status === "PENDING", //if status pending and designation is company then this should be true otherwise false 
            showEyeIcon: true, 
          });
        });
        setRows(responseArray);
        setData(responseArray);
      } else {
        setRows([]);
        setData([]);
      }
    } catch (error) {
      console.log(error);
      setRows([]);
      openPopup("Something went wrong", "Error");
    } finally {
      setLoader(false);
      setPendingApprovals(pendingApprovalCount);
    }
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const handleStatusFilterChange = (newFilter: string) => {
    setStatusFilter(newFilter);
    updateSessionStorageFilter(FilterKeys.PRIMARY_ViewPrimaryOrders , newFilter);
  };

  const filteredData = useMemo(() => {
    const res = filterData(
      data,
      searchedValue.toLowerCase(),
      searchWithStatusFilter,
      statusFilter
    );
    return res;
  }, [searchedValue, statusFilter, data]);


  useEffect(() => {
    if(searchedValue){
      logEvent(analytics, "outlet_approval_searched", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "search_query" : searchedValue,
        "result_count": filteredData.length
      });
      console.log("search-primary",filteredData.length,searchedValue)
      }
  },[searchedValue]);


  useEffect(() => {
    if(!loader) {
      logEvent(analytics, "outlet_approval_list_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_orders_displayed": filteredData.length
      });
      console.log("outlet-approval-list",filteredData.length)
    }
  },[filteredData,loader,statusFilter]);

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);


  const getInitalTableData = async () => {
    if (isProcessing) return; // Prevent if already processing
    setIsProcessing(true);
    try {
      setEndDate(endDate);
      setStartDate(startDate);
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      await getAllOutletForApprovalData(start, end);
      setStatusFilter("ALL")
    } finally {
      setIsProcessing(false);
    }
  };



  const init = async () => {
    if (isProcessing) return; // Prevent if already processing
    setIsProcessing(true);
    try {
      setLoader(true);

      await fetchRequiredConfigs();      // First
      await getInitalTableData();        // Then

    } finally {
      setLoader(false);
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    logLoadTrace(init,"primary_orders_load_time");
  }, []);

  const loadingData = loader// || !stagesRef.current.length;

  const metadata = outletApprovalColumnsMetaData(isCompanyPortal.current);
  return (
    <div className="outlet-approval-parent">
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        modalOpen={genericModalProps?.open}
        onClose={() => setGenericModalProps(initialGenericModalProps)}
      />

       <Header
        title={"New Outlet Approval Status"} 
        searchFunction={loader ? () => { } : handleSearchValueChange} 
        onRefresh={init}
        resetSearch={resetSearch}
        lastRefreshed={lastRefreshed}
      />

      <div className="outlet-approval-filter-bar">
        <div className="outlet-approval-date-action-btn-container">
          <Stack spacing={2} direction={"row"}>
 
            <LocalizationProvider dateAdapter={AdapterDayjs} className="dateEnter">
                <DatePicker
                    label="From Date"
                    inputFormat="DD-MM-YYYY"
                    value={startDate}
                    maxDate={dayjs()}
                    onChange={(value) => {
                    if (value) {
                        setStartDate(value);
                    }
                    }}
                    renderInput={(props) => <TextField {...props} inputProps={{
                    ...props.inputProps,
                    readOnly: true,
                    }} disabled size="small" />}
                />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="To Date"
                    inputFormat="DD-MM-YYYY"
                    value={endDate}
                    maxDate={dayjs()}
                    onChange={(value) => {
                    if (value) {
                        setEndDate(value);
                    }
                    }}
                    renderInput={(props) => <TextField {...props} inputProps={{
                    ...props.inputProps,
                    readOnly: true,
                    }} disabled size="small" />}
                />
            </LocalizationProvider>
          </Stack>

          <Button variant="contained" sx={{ backgroundColor: "#237C9D", borderRadius:"6px",textTransform: "none",boxShadow: "none",  }} onClick={handleApply}>
            Apply
          </Button>
          <Button
            variant="outlined"
            sx={{ backgroundColor: "#F0FBFF", borderRadius:"6px", textTransform: "none",boxShadow: "none"}}
            onClick={handleClearAll}
          >
            Reset
          </Button>

        </div>
        <div className="outlet-approval-filter-container">
          <InputLabel id="primary-filter-dropdown" sx={{color:"#B2B2B2",fontSize:"15px"}}>Filter:</InputLabel>
          <FormControl size="small" sx={{ width: "70%" }}>
            <Select
              labelId="primary-filter-dropdown"
              value={statusFilter}
              onChange={(e) => {
                handleStatusFilterChange(e.target.value);
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#DEDEDE'
                },
              '& .MuiSelect-select': {
        color: '#5C5C5C',      
        fontSize: '14px',      
        fontWeight: 500,       
      },
              }}
            >
              {outletApprovalTrackingStages.map((option) => (
                <MenuItem key={option.id} value={option.id} sx={{
                  color: '#5C5C5C',      
                  fontSize: '14px',      
                  fontWeight: 500,       
                }}>
                  {option.dmsLabel}
                </MenuItem>
              ))}
            </Select>

          </FormControl>
        </div>
      </div>
      <ApprovalSummary totalApprovals={totalApprovals} pendingApprovals={pendingApprovals} />
      {loader? (
        <Loader  sx={{ position: "absolute", top: "55%" }}/>
      ) : (
          <CustomDataGrid
            rows={rows}
            columnsMetaData={metadata}
            orderStatusConfig={outletApprovalTrackingStages}
            redirectUrl="/sale/manageOutletApproval/viewOutletDetails/"
            viewIcon={"eyeIcon"}
            // tableWidth={0.73}
            insideOutletApproval={true}
            height="98"
            orderSourceConfig={orderSourcesRef.current}
          />
      )}
    </div>
  );
};
export default ManageOutletApproval;
